import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Package, FileText, Upload, Star } from 'lucide-react';
import { OrderDetailsDialog } from './OrderDetailsDialog';
import { useBids } from '@/hooks/useBids';
import { useOrders } from '@/hooks/useOrders';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useOrderRating } from '@/hooks/useOrderRating';
import { RatingDialog } from '@/components/rating/RatingDialog';
interface OrderCardProps {
  order: {
    id: string;
    order_number: string;
    pickup_address: string;
    delivery_address: string;
    pickup_date: string;
    delivery_date: string;
    status: string;
    proposed_price?: number;
    final_price?: number;
    weight_kg: number;
    cargo_unit_type?: string;
    pallets_count?: number;
    trailer_type: string;
    marketplace_visible: boolean;
    cargo_title?: string;
    cargo_description?: string;
    pickup_contact: string;
    pickup_phone: string;
    delivery_contact: string;
    delivery_phone: string;
    volume_m3?: number;
    special_instructions?: string;
    start_details?: string;
    delivery_details?: string;
  };
}
const statusColors = {
  draft: 'bg-[#87ADD4]/10 text-[#5A6B7A]',
  posted: 'bg-[#0880AE]/10 text-[#0880AE]',
  booked: 'bg-[#F2AC57]/10 text-[#F2AC57]',
  assigned: 'bg-[#37699E]/10 text-[#37699E]',
  in_transit: 'bg-[#FFBF07]/10 text-[#2D3748]',
  delivered: 'bg-[#14A38B]/10 text-[#14A38B]',
  cancelled: 'bg-[#FF7171]/10 text-[#FF7171]'
};
export const OrderCard = ({
  order
}: OrderCardProps) => {
  const {
    t
  } = useTranslation();
  const navigate = useNavigate();
  const {
    bids
  } = useBids(order.id);
  const {
    updateOrder
  } = useOrders();
  const {
    rating,
    submitRating,
    isSubmitting
  } = useOrderRating(order.id);
  const bidsCount = bids?.length || 0;
  const showBidsLink = order.marketplace_visible && (order.status === 'posted' || order.status === 'booked');
  const isDelivered = order.status === 'delivered';
  const canRate = isDelivered && !rating?.shipper_rating;
  const handlePublishOrder = async () => {
    try {
      await updateOrder({
        id: order.id,
        status: 'posted',
        marketplace_visible: true
      });
      toast.success(t('orders.messages.publishedSuccess'));
    } catch (error) {
      toast.error(t('orders.messages.publishError'));
    }
  };
  const handleSubmitRating = async (ratingValue: number, comment: string) => {
    await submitRating({
      orderId: order.id,
      rating: ratingValue,
      comment,
      ratingType: 'shipper'
    });
  };
  return <Card className="bg-white rounded-xl shadow-md hover:shadow-lg border border-[#DBE2EA] transition-all duration-300">
      <CardHeader className="p-6 pb-3">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <CardTitle className="text-lg font-medium text-[#37699E] font-['Lexend_Deca']">
              {order.order_number}
            </CardTitle>
            <div className="flex gap-2 flex-wrap">
              {canRate && <RatingDialog trigger={<button className="bg-[#FFBF07]/30 text-xs font-semibold px-3 py-1.5 rounded-md flex items-center gap-1 hover:bg-[#FFBF07]/40 transition-all cursor-pointer border border-[#FFBF07]/40 shadow-sm hover:shadow-md text-[#2D3748]">
                      <Star size={12} className="fill-[#FFBF07] text-[#FFBF07]" />
                      {t('rating.toRate')}
                    </button>} onSubmit={handleSubmitRating} isSubmitting={isSubmitting} />}
              {!canRate && rating?.shipper_rating && <RatingDialog trigger={<button className="bg-[#14A38B]/10 text-xs font-semibold px-3 py-1.5 rounded-md flex items-center gap-1 hover:bg-[#14A38B]/20 transition-all cursor-pointer border border-[#14A38B]/30 shadow-sm hover:shadow-md text-[#2D3748]">
                      {[...Array(5)].map((_, i) => <Star key={i} size={12} className={i < rating.shipper_rating! ? "fill-[#FFBF07] text-[#FFBF07]" : "text-[#DBE2EA]"} />)}
                      <span className="ml-1">{rating.shipper_rating}/5</span>
                    </button>} onSubmit={handleSubmitRating} isSubmitting={isSubmitting} defaultRating={rating.shipper_rating} defaultComment={rating.shipper_comment || undefined} />}
            </div>
          </div>
          {(order.final_price || order.proposed_price) && <div className="text-right space-y-2">
              <div className="text-2xl font-semibold text-[#37699E] font-['Lexend_Deca']">
                {order.final_price || order.proposed_price}€
              </div>
              <Badge className={`${statusColors[order.status as keyof typeof statusColors]} text-xs font-medium px-3 py-1.5 rounded-md`}>
                {t(`orders.statuses.${order.status}`)}
              </Badge>
            </div>}
        </div>
      </CardHeader>
      <CardContent className="p-6 pt-0 space-y-3">
        {/* Cargo title */}
        {order.cargo_title && <div className="flex items-center gap-2">
            <Package className="text-[#0880AE] flex-shrink-0" size={16} />
            <span className="text-sm font-medium text-[#2D3748] font-['Funnel_Sans']">{order.cargo_title}</span>
          </div>}

        {/* Weight and cargo units */}
        <div className="flex flex-wrap gap-3 text-sm text-[#5A6B7A] font-['Funnel_Sans']">
          <span>{t('orders.card.weight')}: {order.weight_kg} kg</span>
          {order.cargo_unit_type && order.pallets_count && <span>
              📦 {order.pallets_count}{' '}
              {order.cargo_unit_type === 'palettes' && t('orders.card.pallets')}
              {order.cargo_unit_type === 'caisses' && t('orders.card.boxes')}
              {order.cargo_unit_type === 'euro-palettes' && t('orders.card.euroPallets')}
            </span>}
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <OrderDetailsDialog order={order} />

          {order.status === 'draft' && <Button onClick={handlePublishOrder} className="bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] font-semibold rounded-lg transition-colors duration-200 w-fit">
              <Upload className="mr-2 h-4 w-4" />
              {t('orders.card.publish')}
            </Button>}
          
          {showBidsLink && <Button variant="outline" onClick={() => navigate(`/shipper/orders/${order.id}/bids`)} className="bg-white border-2 border-[#37699E] text-[#37699E] hover:bg-[#EBF4F8] font-semibold rounded-lg transition-colors duration-200 w-fit">
              <FileText className="mr-2 h-4 w-4" />
              {t('orders.card.myOffers')} ({bidsCount})
            </Button>}

          {(order.status === 'assigned' || order.status === 'in_transit' || order.status === 'delivered') && <Button variant="outline" onClick={() => navigate(`/shipper/orders/${order.id}/tracking`)} className="bg-white border-2 border-[#0880AE] text-[#0880AE] hover:bg-[#EBF4F8] font-semibold rounded-lg transition-colors duration-200 w-fit">
              <Package className="mr-2 h-4 w-4" />
              {t('orders.card.track')}
            </Button>}

        </div>
      </CardContent>
    </Card>;
};